import { useState } from "react";
import { useCart } from "./CartContext.jsx";
import VideoUploadStatus from "../Modals/VideoUploadStatus.jsx";

export default function ReviewGrid() {
  const { cart, updateCustomization, removeSingleUnit } = useCart();

  const [editingKey, setEditingKey] = useState(null);
  const [nameDraft, setNameDraft] = useState("");

  const [showVideoUploadModal, setShowVideoUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [activeItemId, setActiveItemId] = useState(null);
  const [activeItemIndex, setActiveItemIndex] = useState(null);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  async function handleVideoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      alert("Video is too large. Max size is 20MB.");
      return;
    }

    const safeFileName = `${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
    const formData = new FormData();
    formData.append("video", file);
    formData.append("filename", safeFileName);

    setUploading(true);
    setUploadProgress(0);
    setUploadComplete(false);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "https://wilcard.introducingjeffrey.com/php/uploadVideo.php");

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percent);
        }
      };

      xhr.onload = () => {
        const result = JSON.parse(xhr.responseText);
        if (result.success) {
          updateCustomization(activeItemId, activeItemIndex, {
            ...(cart.find(p => p.id === activeItemId)?.customization?.[activeItemIndex] || {}),
            video: result.url,
          });
          setUploadComplete(true);
          setTimeout(() => {
            setUploading(false);
            setUploadProgress(0);
            setShowVideoUploadModal(false);
          }, 1000);
        } else {
          alert("❌ Upload failed: " + result.message);
          setUploading(false);
        }
      };

      xhr.onerror = () => {
        alert("❌ Upload error.");
        setUploading(false);
      };

      xhr.send(formData);
    } catch (err) {
      console.error("Upload error:", err);
      setUploading(false);
    }
  }

  return (
    <div className="review-order-grid">
      {cart.flatMap(item =>
        Array.from({ length: item.quantity }, (_, i) => {
          const key = `${item.id}-${i}`;
          const c = item.customization?.[i] || {};
          const isEditing = editingKey === key;

          const renderNameRow = () => {
            if (!item.customName) return null;
            if (isEditing) {
              return (
                <input
                  className="review-name-input"
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                />
              );
            }
            return (
              <p className="review-line">
                Happy&nbsp;Birthday <strong>{c.recipientName || "…"}</strong>
              </p>
            );
          };

          const renderVideo = () => {
            if (!item.customVideo) return null;
            return c.video ? (
              <button
                className="preview-button"
                onClick={() => {
                  setPreviewUrl(c.video);
                  setShowPreviewModal(true);
                }}
              >
                Preview
              </button>
            ) : (
              <p className="review-line"><em>No video uploaded.</em></p>
            );
          };

          const renderNoCustom = () =>
            !item.customName && !item.customVideo && (
              <p className="review-line"><em>No customization options available.</em></p>
            );

          const actions = [];

          actions.push({
            icon: "🗑️",
            onClick: () => {
              if (window.confirm(`Remove "${item.UITitle}" from cart?`)) {
                removeSingleUnit(item.id);
              }
            }
          });

          if (item.customName) {
            if (isEditing) {
              actions.push({
                icon: "💾",
                onClick: () => {
                  updateCustomization(item.id, i, {
                    ...c,
                    recipientName: nameDraft
                  });
                  setEditingKey(null);
                }
              });
            } else {
              actions.push({
                icon: "✏️",
                onClick: () => {
                  setEditingKey(key);
                  setNameDraft(c.recipientName || "");
                }
              });
            }
          }

          if (item.customVideo) {
            actions.push({
              icon: "📹",
              onClick: () => {
                setActiveItemId(item.id);
                setActiveItemIndex(i);
                setShowVideoUploadModal(true);
              }
            });
          }

          return (
            <div className="review-card" key={key}>
              <img src={item.imageUrl} alt={item.UITitle} className="review-thumb" />
              <h5 className="review-title">{item.UITitle}</h5>
              {renderNameRow()}
              {renderVideo()}
              {renderNoCustom()}
              <div className="review-actions">
                {actions.map(({ icon, onClick }, idx) => (
                  <button key={idx} onClick={onClick}>{icon}</button>
                ))}
              </div>
            </div>
          );
        })
      )}

      <VideoUploadStatus
        show={uploading}
        uploading={uploading}
        progress={uploadProgress}
        completed={uploadComplete}
        onCancel={() => window.location.reload()}
        onClose={() => {
          setUploading(false);
          setUploadProgress(0);
          setUploadComplete(false);
        }}
      />

      {showVideoUploadModal && (
        <div className="modal-wrapper">
          <div className="modal-shell">
            <h3>Upload a Video Message</h3>
            <p>
              Keep it short.<br /> (about 10–15 seconds)<br />
              <span className="checkout-summary">Max size: 20MB</span>
            </p>

            <label className="wizard-button" style={{ cursor: "pointer" }}>
              Choose Video
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/3gpp"
                style={{ display: "none" }}
                onChange={handleVideoUpload}
              />
            </label>

            <button className="wizard-button" onClick={() => setShowVideoUploadModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

    {showPreviewModal && previewUrl && (
      <div className="modal-wrapper" onClick={() => setShowPreviewModal(false)}>
        <div className="modal-shell" onClick={(e) => e.stopPropagation()}>
          <video
            src={previewUrl}
            controls
            style={{ maxWidth: "100%", borderRadius: "8px" }}
          />
          <button
            className="wizard-button"
            style={{ marginTop: "1rem" }}
            onClick={() => setShowPreviewModal(false)}
          >
            Close Preview
          </button>
        </div>
      </div>
    )}

    </div>
  );
}
